import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from './useNostrPublish';
import { useCurrentUser } from './useCurrentUser';
import { DISCOVERY_RELAYS, discoverySignal } from '@/lib/appRelays';
import {
  BUSINESS_VERIFY_KIND,
  BUSINESS_VERIFY_CLAIM_D,
  BUSINESS_VERIFY_CLAIM_TAG,
  BUSINESS_VERIFY_GRANT_TAG,
  VERIFY_GRANT_ISSUERS,
  parseVerifyClaim,
  parseVerificationGrant,
  computeVerificationState,
  buildVerifyGrantEvent,
  type VerifyClaim,
  type VerificationGrant,
  type VerificationState,
} from '@/lib/businessVerify';

/** A business's verification review record for the admin panel. */
export interface VerificationReview {
  businessPubkey: string;
  claim: VerifyClaim | null;
  grant: VerificationGrant | null;
  state: VerificationState;
}

/**
 * Fetch every verification claim and grant, then build a per-business review
 * record. Newest claim + newest grant win per pubkey.
 */
export function useVerificationReviews() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nostr', 'verification-reviews'],
    queryFn: async (c): Promise<VerificationReview[]> => {
      const group = nostr.group(DISCOVERY_RELAYS);

      const [claimEvents, grantEvents] = await Promise.all([
        group.query(
          [{
            kinds: [BUSINESS_VERIFY_KIND],
            '#t': [BUSINESS_VERIFY_CLAIM_TAG],
            '#d': [BUSINESS_VERIFY_CLAIM_D],
            limit: 1000,
          }],
          { signal: discoverySignal(c.signal) },
        ),
        group.query(
          [{
            kinds: [BUSINESS_VERIFY_KIND],
            authors: VERIFY_GRANT_ISSUERS,
            '#t': [BUSINESS_VERIFY_GRANT_TAG],
            limit: 1000,
          }],
          { signal: discoverySignal(c.signal) },
        ),
      ]);

      // Newest claim per business pubkey.
      const claimByBiz = new Map<string, VerifyClaim>();
      for (const ev of claimEvents) {
        const claim = parseVerifyClaim(ev);
        if (!claim) continue;
        const existing = claimByBiz.get(claim.pubkey);
        if (!existing || claim.claimedAt > existing.claimedAt) {
          claimByBiz.set(claim.pubkey, claim);
        }
      }

      // Newest grant per business pubkey.
      const grantByBiz = new Map<string, VerificationGrant>();
      for (const ev of grantEvents) {
        const grant = parseVerificationGrant(ev);
        if (!grant) continue;
        const existing = grantByBiz.get(grant.businessPubkey);
        if (!existing || grant.issuedAt > existing.issuedAt) {
          grantByBiz.set(grant.businessPubkey, grant);
        }
      }

      // Union of all businesses that have a claim or a grant.
      const pubkeys = new Set<string>([...claimByBiz.keys(), ...grantByBiz.keys()]);
      const reviews: VerificationReview[] = [];
      for (const pubkey of pubkeys) {
        const claim = claimByBiz.get(pubkey) ?? null;
        const grant = grantByBiz.get(pubkey) ?? null;
        reviews.push({
          businessPubkey: pubkey,
          claim,
          grant,
          state: computeVerificationState(grant, claim),
        });
      }

      // Sort: pending review first, then verified, then the rest; newest claim first.
      const order: Record<VerificationState['status'], number> = {
        PENDING: 0,
        VERIFIED: 1,
        EXPIRED: 2,
        UNVERIFIED: 3,
      };
      reviews.sort((a, b) => {
        const byStatus = order[a.state.status] - order[b.state.status];
        if (byStatus !== 0) return byStatus;
        const aAt = a.claim?.claimedAt ?? a.grant?.issuedAt ?? 0;
        const bAt = b.claim?.claimedAt ?? b.grant?.issuedAt ?? 0;
        return bAt - aAt;
      });

      return reviews;
    },
    staleTime: 60 * 1000,
  });
}

/** Publish a verification grant (issue or revoke) as the logged-in issuer. */
export function useIssueGrant() {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (opts: {
      businessPubkey: string;
      txid?: string;
      revoked?: boolean;
    }) => {
      if (!user) throw new Error('Not logged in');
      const { kind, tags, content } = buildVerifyGrantEvent({
        businessPubkey: opts.businessPubkey,
        issuedBy: user.pubkey,
        txid: opts.txid,
        revoked: opts.revoked,
      });
      return publishEvent({ kind, tags, content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nostr', 'verification-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'bchnostr-verified'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'business-verification'] });
    },
  });
}
